import { Tool, ToolFormValues } from "@/types";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useState } from "react";
import { toast } from "sonner";
import { BasicInfoSection } from "./form-sections/BasicInfoSection";
import { CategoriesSection } from "./form-sections/CategoriesSection";
import { AdditionalInfoSection } from "./form-sections/AdditionalInfoSection";
import { FormActions } from "./form-sections/FormActions";
import { ScrollArea } from "@/components/ui/scroll-area";

const toolSchema = z.object({
  name: z.string()
    .min(3, "يجب أن يكون الاسم 3 أحرف على الأقل")
    .max(100, "الاسم طويل جداً"),
  description: z.string()
    .min(10, "يجب أن يكون الوصف 10 أحرف على الأقل")
    .max(1000, "الوصف طويل جداً"),
  imageUrl: z.string()
    .url("يجب إدخال رابط صورة صحيح")
    .regex(/^https?:\/\/.+$/, "يجب أن يبدأ الرابط بـ http:// أو https://"),
  categories: z.array(z.string())
    .min(1, "يجب اختيار تصنيف واحد على الأقل"),
  is_featured: z.boolean().default(false),
  usage_guide: z.string().optional(),
  tutorial_video_url: z.string()
    .url("يجب إدخال رابط فيديو صحيح")
    .optional()
    .or(z.literal("")),
  tool_url: z.string()
    .url("يجب إدخال رابط صحيح")
    .optional()
    .or(z.literal("")),
  examples: z.array(
    z.object({
      title: z.string(),
      description: z.string()
    })
  ).default([])
});

interface ToolFormProps {
  onSubmit: (values: ToolFormValues) => Promise<void>;
  initialData?: Tool;
}

export const ToolForm = ({ onSubmit, initialData }: ToolFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ToolFormValues>({
    resolver: zodResolver(toolSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      imageUrl: initialData?.imageUrl || "",
      categories: initialData?.categories || [],
      is_featured: initialData?.is_featured || false,
      usage_guide: initialData?.usage_guide || "",
      tutorial_video_url: initialData?.tutorial_video_url || "",
      tool_url: initialData?.tool_url || "",
      examples: initialData?.examples || []
    },
  });

  const handleSubmit = async (values: ToolFormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(values);
      toast.success(initialData ? "تم تحديث الأداة بنجاح" : "تم إضافة الأداة بنجاح");
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : "حدث خطأ أثناء حفظ الأداة"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-6">
            <div className="grid gap-6 p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
              <BasicInfoSection form={form} />
              <CategoriesSection form={form} />
              <AdditionalInfoSection form={form} />
            </div>
          </div>
        </ScrollArea>
        <FormActions isSubmitting={isSubmitting} isEditing={!!initialData} />
      </form>
    </Form>
  );
};